import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from 'react-spring';
import { IoClose, IoCheckmark, IoSend } from 'react-icons/io5';
import { FiSearch } from 'react-icons/fi';
import { shareService } from '../../services/api';
import { labelForStaffRole } from '../../constants/staffRoles';
import toast from 'react-hot-toast';

// Add styles for scrollbar hiding
const styles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

const ShareActionSheet = ({ isOpen, onClose, onShare, customShareHandler, title = "Share", shareUrl = "", shareType = "player_public_profile", shareData = {}, preview = null }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [messageText, setMessageText] = useState('');

  // Inject styles for scrollbar hiding
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Fetch recipients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRecipients();
    }
  }, [isOpen]);

  // Fetch recipients from API
  const fetchRecipients = async (query = '') => {
    setLoading(true);
    try {
      const response = await shareService.getRecipients(query);
      if (response.success) {
        // Transform API response to match component expectations
        const transformedUsers = response.data.map(user => ({
          id: user.account_id,
          name: user.name,
          username: user.email,
          avatar: user.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=2283FF&color=fff&size=150`,
          isOnline: false, // API doesn't provide online status
          // T039: the recipients endpoint sends the raw role_type enum
          // ("assistant_coach"). Humanize once here, through the shared
          // staff-role catalog, so every consumer of the recipient object
          // (row label included) reads the same label. Non-enum values the
          // endpoint already sends humanized ("Agent", "Staff") pass through
          // the catalog's fallback unchanged.
          role: labelForStaffRole(user.role) || user.role,
          agency_name: user.agency_name,
          club_name: user.club_name,
          channel_id_candidate: user.channel_id_candidate,
          // Present only on a CROSS-ORG row, where it is the id of a conversation
          // both sides are already in. It decides which branch the send takes
          // below — see handleShare.
          channel_id: user.channel_id || null
        }));
        setUsers(transformedUsers);
      } else {
        toast.error(response.error || 'Failed to fetch recipients');
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };

  // Handle search with debouncing
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      if (searchQuery !== '') {
        setSearchLoading(true);
        fetchRecipients(searchQuery).finally(() => setSearchLoading(false));
      } else {
        fetchRecipients();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen]);

  // Filter users based on search query (client-side filtering for better UX)
  const filteredUsers = useMemo(() => {
    if (searchQuery.trim() === '') {
      return users;
    } else {
      // Null-safe: the endpoint may return a row without an email, and an
      // undefined username used to take the whole picker down with a TypeError.
      const needle = searchQuery.toLowerCase();
      return users.filter(user =>
        (user.name || "").toLowerCase().includes(needle) ||
        (user.username || "").toLowerCase().includes(needle)
      );
    }
  }, [searchQuery, users]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      setSearchQuery('');
      setMessageText('');
      setUsers([]);
    }
  }, [isOpen]);

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.find(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) return;

    // Injection point: when a caller supplies `customShareHandler`, it owns the
    // send (its own backend + toasts) and this sheet only manages open/close +
    // spinner. Callers that don't pass it keep the built-in shareService path
    // below unchanged (byte-for-byte) — note the legacy `onShare` prop stays
    // ignored, as before, so the profile pages that still pass it are untouched.
    // This is the thin adapter the Radar share uses to reuse the platform picker
    // without forking it.
    if (typeof customShareHandler === 'function') {
      try {
        setLoading(true);
        const result = await customShareHandler(selectedUsers, messageText);
        if (result?.success !== false) onClose();
      } catch (error) {
        console.error('Error sharing:', error);
        toast.error('Failed to share. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);

      if (selectedUsers.length === 1) {
        // Single share. A row that carries a channel_id is a cross-org chat
        // partner: address the EXISTING conversation, which the backend gates on
        // caller membership. The account_id form stays for colleagues — it is
        // the branch that may create a channel, and it is deliberately same-org
        // only, so sending it for a cross-org row would just 403.
        const user = selectedUsers[0];
        const target = user.channel_id ? { channel_id: user.channel_id } : { account_id: user.id };

        const response = await shareService.shareSingle(
          target,
          shareType,
          shareUrl,
          shareData,
          messageText
        );
        
        if (response.success) {
          // Trigger notification for the recipient
          const notificationText = messageText || `Shared ${shareType.replace(/_/g, ' ')} with you`;
          await shareService.notifyMessage(user.id, notificationText, response.channelId);
          
          toast.success('Shared successfully!');
          onClose();
        } else {
          toast.error(response.error || 'Failed to share');
        }
      } else {
        // Bulk share. Same rule per recipient; account_id rides along on a
        // cross-org row too, so the response still names who was reached.
        const recipients = selectedUsers.map(user => (
          user.channel_id
            ? { account_id: user.id, channel_id: user.channel_id }
            : { account_id: user.id }
        ));

        const response = await shareService.shareBulk(
          recipients,
          shareType,
          shareUrl,
          shareData,
          messageText
        );
        
        if (response.success) {
          // Trigger notifications for successful recipients.
          // `POST /chat/share/bulk/` answers { results, failures } and every
          // entry in `results` IS a success (anything that failed lands in
          // `failures`), so an entry carries { account_id, channel_id,
          // message_id } — no per-entry `success` flag and no
          // `recipient_account_id`. This used to read `result.success &&
          // result.recipient_account_id`, two fields the endpoint has never
          // returned, so the bulk echo never fired at all. The single-share
          // path above already reads the shape the endpoint really sends.
          const notificationText = messageText || `Shared ${shareType.replace(/_/g, ' ')} with you`;
          const notificationPromises = response.data.map(async (result) => {
            // notify-message is channel-scoped server-side (it 400s without a
            // channel_id), so both ids must be present before we echo.
            if (result.account_id && result.channel_id) {
              return shareService.notifyMessage(result.account_id, notificationText, result.channel_id);
            }
          });
          
          // Wait for all notifications to be sent (don't block UI)
          Promise.all(notificationPromises).catch(error => {
            console.error('Error sending notifications:', error);
          });
          
          const successCount = response.data.length;
          const failureCount = response.failures.length;
          
          if (failureCount === 0) {
            toast.success(`Shared with ${successCount} recipients successfully!`);
          } else {
            toast.success(`Shared with ${successCount} recipients. ${failureCount} failed.`);
          }
          onClose();
        } else {
          toast.error(response.error || 'Failed to share');
        }
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const sheetVariants = {
    hidden: {
      y: '100%',
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300
      }
    },
    exit: {
      y: '100%',
      opacity: 0,
      transition: {
        type: 'spring',
        damping: 25,
        stiffness: 300
      }
    }
  };

  const UserItem = ({ user, isSelected, onToggle }) => {
    const itemSpring = useSpring({
      transform:  'scale(1)',
      backgroundColor: isSelected ? 'rgba(34, 131, 255, 0.05)' : 'rgba(255, 255, 255, 0)',
      config: { tension: 300, friction: 20 }
    });

    const checkboxSpring = useSpring({
      transform: isSelected ? 'scale(1)' : 'scale(0.8)',
      backgroundColor: isSelected ? '#2283FF' : '#F3F4F6',
      config: { tension: 400, friction: 25 }
    });

    return (
      <animated.div
        style={itemSpring}
        className="flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer border border-transparent hover:border-gray-100"
        onClick={() => onToggle(user)}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={user.avatar}
              alt={user.name}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=2283FF&color=fff&size=150`;
              }}
            />
            {user.isOnline && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
              />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{user.name}</p>
            <p className="text-xs text-gray-500">{user.username}</p>
            {user.role && (
              <p className="text-xs text-blue-600 font-medium">{user.role}</p>
            )}
          </div>
        </div>
        
        <animated.div
          style={checkboxSpring}
          className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
        >
          <AnimatePresence mode="wait">
            {isSelected && (
              <motion.div
                initial={{ scale: 0, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0, opacity: 0, rotate: 180 }}
                transition={{ duration: 0.2, type: "spring", stiffness: 300 }}
              >
                <IoCheckmark className="text-white text-lg" />
              </motion.div>
            )}
          </AnimatePresence>
        </animated.div>
      </animated.div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Action Sheet */}
          <motion.div
            variants={sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 ${
              selectedUsers.length > 0 ? 'max-h-[90vh]' : 'max-h-[80vh]'
              } flex flex-col`}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <IoClose className="text-xl text-gray-600" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="relative">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                  <input
                    type="text"
                    placeholder="Search people..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all duration-200 text-gray-900 placeholder-gray-500"
                  />
                  {searchQuery && (
                    <motion.button
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <IoClose className="text-gray-400 text-lg" />
                    </motion.button>
                  )}
                </motion.div>
              </div>
            </div>

            {/* Message Input */}
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/30">
              <input
                type="text"
                placeholder="Add a message (optional)..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="w-full px-4 py-2 bg-white rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none transition-all duration-200 text-gray-900 placeholder-gray-500 text-sm"
              />
            </div>

            {/* Optional preview slot — additive: when a caller passes a `preview`
                node (e.g. the club radar read-only snapshot card, design 1n/1o) it
                renders here. Default null ⇒ byte-identical for every existing
                caller (agent radar adapter, player-profile / friendlies / LM share). */}
            {preview && (
              <div className="px-6 py-3 border-b border-gray-100 bg-white">
                {preview}
              </div>
            )}

            {/* Selected Users Preview */}
            <AnimatePresence>
              {selectedUsers.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="px-6 border-b border-gray-100  bg-blue-50/30"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700">
                      Selected ({selectedUsers.length})
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedUsers([])}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear all
                    </motion.button>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pt-2 px-1 scrollbar-hide">
                    <AnimatePresence>
                      {selectedUsers.map((user, index) => (
                        <motion.div
                          key={user.id}
                          initial={{ scale: 0, opacity: 0, x: -20 }}
                          animate={{ 
                            scale: 1, 
                            opacity: 1, 
                            x: 0,
                            transition: { delay: index * 0.1 }
                          }}
                          exit={{ scale: 0, opacity: 0, x: 20 }}
                          className="flex-shrink-0 text-center relative group"
                        >
                          <div className="relative">
                            <img
                              src={user.avatar}
                              alt={user.name}
                              className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-200 shadow-sm"
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=2283FF&color=fff&size=150`;
                              }}
                            />
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleUserSelection(user);
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                            >
                              <IoClose className="text-white text-sm" />
                            </motion.button>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 max-w-16 truncate font-medium">
                            {user.name.split(' ')[0]}
                          </p>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Users List */}
            <div className="flex-1 overflow-y-auto px-6 py-2 max-h-96">
              {loading && !searchLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-500">Loading recipients...</span>
                </div>
              ) : (
                <div className="space-y-1">
                  <AnimatePresence>
                    {filteredUsers.map((user, index) => (
                      <motion.div
                        key={user.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ 
                          opacity: 1, 
                          y: 0,
                          transition: { delay: index * 0.05 }
                        }}
                        exit={{ opacity: 0, x: -20 }}
                      >
                        <UserItem
                          user={user}
                          isSelected={selectedUsers.find(u => u.id === user.id)}
                          onToggle={toggleUserSelection}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
              
              {searchLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-500 text-sm">Searching…</span>
                </div>
              )}
              
              {filteredUsers.length === 0 && searchQuery && !searchLoading && !loading && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiSearch className="text-2xl text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No users found</p>
                  <p className="text-gray-400 text-sm mt-1">Try searching with a different name</p>
                </motion.div>
              )}
              
              {filteredUsers.length === 0 && !searchQuery && !loading && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IoSend className="text-2xl text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No contacts available</p>
                  <p className="text-gray-400 text-sm mt-1">No recipients found for your role</p>
                </motion.div>
              )}
            </div>

            {/* Send Button */}
            <div className="p-6 border-t border-gray-100 bg-white">
              <motion.button
                whileHover={selectedUsers.length > 0 && !loading ? { scale: 1.02 } : {}}
                whileTap={selectedUsers.length > 0 && !loading ? { scale: 0.98 } : {}}
                onClick={handleShare}
                disabled={selectedUsers.length === 0 || loading}
                className={`w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-3 transition-all duration-300 ${
                  selectedUsers.length > 0 && !loading
                    ? 'bg-[#2283FF] hover:bg-blue-600 cursor-pointer shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sharing…</span>
                  </>
                ) : (
                  <>
                    <motion.div
                      animate={selectedUsers.length > 0 ? { 
                        rotate: [0, -10, 10, 0],
                        transition: { duration: 0.5, repeat: Infinity, repeatDelay: 2 }
                      } : {}}
                    >
                      <IoSend className="text-lg" />
                    </motion.div>
                    <span>
                      {selectedUsers.length === 0 
                        ? 'Select users to share' 
                        : `Send to ${selectedUsers.length} ${selectedUsers.length === 1 ? 'person' : 'people'}`
                      }
                    </span>
                    {selectedUsers.length > 0 && (
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white bg-opacity-20 px-2 py-1 rounded-full text-sm font-bold"
                      >
                        {selectedUsers.length}
                      </motion.div>
                    )}
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareActionSheet;
